import { Search } from '@/components/Search';
import { LoadingBox } from '@/components/UI/Box';
import { ColoredCircularProgress } from '@/components/UI/Typography';
import {
  IconCancelMS,
  IconCheckCircleMS,
  IconCloseMS,
  IconDraftsMS,
  IconErrorMS,
} from '@/constants/icons';
import { Button } from '@/pages/Oversight/components/Button';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';

import { Constants } from '../../../../constants/oversight';
import {
  getNotificationStatus,
  notificationFilters,
  notificationSeverities,
  NotificationSeverity,
  notificationTitles,
} from './data';
import {
  CardClient,
  CardIcon,
  CardMeta,
  CardText,
  CardTime,
  CardTitle,
  ChipList,
  CloseButton,
  Container,
  EmptyMessage,
  FilterChip,
  GroupLabel,
  Header,
  LoadMoreBox,
  NotificationCard,
  PanelTitle,
  ReadButton,
  ScrollArea,
  SearchBox,
  UnreadDot,
} from './styles';
import { useNotifications } from './useNotifications';

interface NotificationsProps {
  unreadCount: number;
  onClose: () => void;
  onRead: () => void;
}

const severityIcons: Record<NotificationSeverity, JSX.Element> = {
  success: IconCheckCircleMS,
  error: IconCancelMS,
  warning: IconErrorMS,
  info: IconErrorMS,
};

export function Notifications({
  unreadCount,
  onClose,
  onRead,
}: NotificationsProps): JSX.Element {
  const {
    search,
    setSearch,
    activeFilter,
    setActiveFilter,
    groups,
    loading,
    hasMore,
    handleLoadMore,
    handleRead,
    handleOpenClient,
  } = useNotifications(onRead);

  return (
    <Container>
      <Header>
        <CloseButton
          aria-label={Constants.notifications_close}
          onClick={onClose}
        >
          {IconCloseMS}
        </CloseButton>

        <PanelTitle>{`${Constants.notifications} (${unreadCount})`}</PanelTitle>

        <SearchBox>
          <Search
            value={search}
            setSearchField={setSearch}
            placeholder={Constants.notifications_search}
            maxLength={60}
            width="100%"
          />
        </SearchBox>

        <ChipList>
          {notificationFilters.map(({ value, label }) => (
            <FilterChip
              key={value}
              isActive={activeFilter === value}
              onClick={() => setActiveFilter(value)}
            >
              {label}
            </FilterChip>
          ))}
        </ChipList>
      </Header>

      <ScrollArea>
        {loading && groups.length === 0 && (
          <LoadingBox>
            <ColoredCircularProgress />
          </LoadingBox>
        )}

        {!loading && groups.length === 0 && (
          <EmptyMessage>{Constants.notifications_empty}</EmptyMessage>
        )}

        {groups.map(({ label, items }) => (
          <div key={label}>
            <GroupLabel>{label}</GroupLabel>
            {items.map((notification) => {
              const status = getNotificationStatus(notification.type);
              const isRead = Boolean(notification.read_at);
              // Lida não pede mais atenção: perde a cor e o ícone da situação.
              const severity =
                isRead || !status ? 'info' : notificationSeverities[status];

              return (
                <NotificationCard
                  key={notification.id}
                  isRead={isRead}
                  onClick={() => handleOpenClient(notification.client_id)}
                >
                  <CardIcon severity={severity} isRead={isRead}>
                    {severityIcons[severity]}
                  </CardIcon>
                  <CardText>
                    <CardTitle>
                      {status ? notificationTitles[status] : notification.type}
                    </CardTitle>
                    <CardClient>
                      {`${Constants.notifications_client} ${notification.client_name}`}
                    </CardClient>
                  </CardText>
                  <CardMeta className="notification-meta">
                    <CardTime>
                      {dayjs(notification.occurred_at).format('HH:mm')}
                    </CardTime>
                    {!isRead && <UnreadDot />}
                  </CardMeta>
                  {!isRead && (
                    <Tooltip
                      title={Constants.notifications_mark_as_read}
                      placement="top"
                    >
                      <ReadButton
                        className="notification-read"
                        aria-label={Constants.notifications_mark_as_read}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRead(notification);
                        }}
                      >
                        {IconDraftsMS}
                      </ReadButton>
                    </Tooltip>
                  )}
                </NotificationCard>
              );
            })}
          </div>
        ))}

        {hasMore && (
          <LoadMoreBox>
            <Button variant="text" onClick={handleLoadMore} disabled={loading}>
              {Constants.notifications_load_more}
            </Button>
          </LoadMoreBox>
        )}
      </ScrollArea>
    </Container>
  );
}
